<template>
    <button @click="show = true" class="btn btn-lg btn-yellow">{{ text }}</button>

    <modal id="affiliateModal" v-if="show" @close="show = false">
        <template #header>CommodityTrends Affiliate Program</template>
        <template #body>
            <div class="py-2">
                <p class="paragraph">Tell us about yourself. If qualified, our affiliate manager will be in contact to discuss details of the program.</p>

                <div class="flex flex-wrap -mx-2">
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="name">Name<span>*Required.</span></label>
                        <input @input="errors.name = false" :class="{'is-invalid': errors.name}" v-model="form.name" class="form-input" id="name" type="text" placeholder="">
                        <div class="error" v-if="errors.name"> {{ errors.name }}</div>
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="email">Email<span>*Required.</span></label>
                        <input @input="errors.email = false" :class="{'is-invalid': errors.email}" v-model="form.email" class="form-input" id="email" type="email" placeholder="">
                        <div class="error" v-if="errors.email"> {{ errors.email }}</div>
                    </div>

                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="phone">Phone Number<span>*Required.</span></label>
                        <input @input="errors.phone = false" :class="{'is-invalid': errors.phone}" v-model="form.phone" class="form-input" id="phone" type="text" placeholder="">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="country">Country</label>
                        <input v-model="form.country" class="form-input" id="country" type="text" placeholder="">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="website">Website</label>
                        <input v-model="form.website" class="form-input" id="website" type="text" placeholder="">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="companyname">Company Name</label>
                        <input v-model="form.companyname" class="form-input" id="companyname" type="text" placeholder="">
                    </div>

                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="twitter">Twitter Profile URL<span>*Required.</span></label>
                        <input @input="errors.twitter = false" :class="{'is-invalid': errors.twitter}" v-model="form.twitter" class="form-input" id="twitter" type="text" placeholder="https://twitter.com/your_username">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="facebook">Facebook Profile URL</label>
                        <input v-model="form.facebook" class="form-input" id="facebook" type="text" placeholder="https://facebook.com/your_profile">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="youtube">YouTube Channel URL</label>
                        <input v-model="form.youtube" class="form-input" id="youtube" type="text" placeholder="https://youtube.com/your_channel">
                    </div>
                    <div class="w-full md:w-1/2 px-2">
                        <label class="form-label-class" for="instagram">Instagram Page URL</label>
                        <input v-model="form.instagram" class="form-input" id="instagram" type="text" placeholder="https://instagram.com/your_username">
                    </div>
                </div>
                <div class="w-full mt-4 mb-4" v-if="!loggedIn">
                    <div id="captcha"></div>
                    <div class="error" v-if="errors.captcha">The reCAPTCHA was not entered correctly</div>
                </div>
                <div class="paragraph">
                    <small>By clicking below, you indicate that you have read and agree to the <a href="https://www.commoditytrends.com/terms-of-service" target="_blank">Terms of Service</a> and <a href="https://www.commoditytrends.com/terms-of-service#privacy-policy" target="_blank">Privacy Policy</a>.</small>
                </div>
            </div>
        </template>
        <template #footer>
            <div class="flex justify-center">
                <div class="btn btn-sm btn-yellow-ghost inline-flex mx-1" @click="show = false">Cancel</div>
                <div class="btn btn-sm btn-yellow inline-flex mx-1" @click="send">YES! Get Started!</div>
            </div>
        </template>
    </modal>

    <flash-message ref="flash" :seconds-to-display="6">
        <template #body>
            Your message has been sent! A response will be sent to {{ displayEmail }} shortly.
        </template>
    </flash-message>
</template>

<script setup>
import { ref, watch } from 'vue';

const props = defineProps({
    text: String,
    siteKey: String,
});

const flash = ref(null);
const show = ref(false);
const loading = ref(false);
const loggedIn = ref((window.currentUser && window.currentUser.username) ? true : false);
// const captchaValid = ref(false);
const displayEmail = ref('');
const errors = ref({
    name: false,
    email: false,
    phone: false,
    twitter: false,
    captcha: false,
});

const form = ref({
    name: '',
    email: (window.currentUser && window.currentUser.email) || '',
    phone: '',
    country: 'USA',
    website: '',
    companyname: '',
    twitter: '',
    facebook: '',
    youtube: '',
    instagram: '',
    captcha: '',
});

const validate = () => {
    let valid = true;
    if (loggedIn.value) form.value.captcha = true;
    Object.keys(errors).map((formName) => {
        if (form[formName] === '') {
            errors[formName] = 'Please fill this field';
            valid = false;
        }
    });
    return valid;
};

const updateCaptcha = (response) => {
    form.value.captcha = response;
};

const reset = () => {
    Object.keys(form).map((formName) => {
        form[formName] = '';
    });
    if (!loggedIn.value) {
        grecaptcha.reset();
    }
};

const send = () => {
    const route = loggedIn.value ? window.route('post.affiliate-logged-in') : window.route('post.affiliate');

    if (!validate()) return;

    loading.value = true;

    const params = {
        ...form,
        user: window.currentUser,
        resolution: {
            height: window.innerHeight,
            width: window.innerWidth,
        },
    };

    axios.post(route, params)
        .then((response) => {
            displayEmail.value = form.value.email;
            reset();
            flash.value.display();
            show.value = false;
        }).catch((error) => {
            flash.value.error('Error sending your affiliate');
            Object.keys(error.response.data.errors).map((formName) => {
                errors[formName] = error.response.data.errors[formName][0];
            });
        }).then(() => {
            loading.value = false;
        });
};

watch(show, (nval, oval) => {
    if (nval) {
        if (loggedIn.value) return;
        setTimeout(() => {
            grecaptcha.render('captcha', {
                sitekey: props.siteKey,
                callback: updateCaptcha,
            });
        }, 500);
    }
});
</script>

<style lang="scss">
#affiliateModal {
    @apply text-left;
    .modal-container {
        width: 48rem;
        @screen xs {
            width: 100%;
        }
    }

    .form-input {
        margin-bottom: 0.1rem;
    }

    .form-label-class {
        margin-top: 0.5rem;
    }

    label span {
        font-size: 80%;
        font-style: italic;
        color: rgb(224, 63, 31);
        margin-left: 0.25rem;
        vertical-align: middle;
    }
}
</style>
